import {
  DynamicSignatureFormData,
  DynamicSignatureFormQuestion,
  FormSearchResponse,
  FormTableResponse,
  ImportFormTemplateBody,
  ImportFormTemplateResponse,
  RecommendedTemplate,
  TaskForm,
  TaskFormEmailInfo,
  TaskFormFolder,
  TaskFormSignatureDataGroups,
  TaskFormSignatureDataGroupsBody,
  TemplateUseCase,
  ValidateDynamicSignatureQuestionModificationResponse,
} from './../types';
import type { FormTableSortColumn, FormTableSortOrder } from './../types';
import { client } from './index';

/**
 * DS-3978. The four values the service recognizes, keyed by column and
 * direction. The unsorted state emits nothing, which keeps the request of a
 * table nobody sorted byte for byte the one the client sends today.
 *
 * Two dimensions and not one: the mobile users screen sorts by a single column,
 * so its map is keyed by direction alone. Here the header offers Name and
 * Status, and the service takes one parameter for both.
 *
 * `status_asc` is active forms first, matching the service.
 */
type FormTableSortParam = 'name_asc' | 'name_desc' | 'status_asc' | 'status_desc';

const SORT_PARAM_BY_COLUMN_AND_ORDER: Record<
  FormTableSortColumn,
  Record<'asc' | 'desc', FormTableSortParam>
> = {
  name: { asc: 'name_asc', desc: 'name_desc' },
  status: { asc: 'status_asc', desc: 'status_desc' },
};

function sortParamOf(sortOrder?: FormTableSortOrder): FormTableSortParam | undefined {
  if (!sortOrder || sortOrder.order === 'unsorted') return undefined;
  return SORT_PARAM_BY_COLUMN_AND_ORDER[sortOrder.column][sortOrder.order];
}

interface GetTaskFormsParams {
  page: number;
  folderId?: number;
  search?: string;
  sortOrder?: FormTableSortOrder;
}

async function getTaskForms({ page, folderId, search, sortOrder }: GetTaskFormsParams) {
  const params = new URLSearchParams();

  params.append('page', page.toString());
  if (folderId) params.append('folder_id', folderId.toString());
  if (search) params.append('search', search);
  const sort = sortParamOf(sortOrder);
  if (sort) params.append('sort', sort);

  const response = await client.get<FormTableResponse>(`/task_forms.json?${params.toString()}`);
  const { data } = response;
  return data;
}

// This request gets ALL task forms that the account has. If you use this, you MUST use virtualization.
async function getAllTaskForms(search?: string, folderId?: number, includeDeleted?: boolean) {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (folderId) params.append('folder_id', folderId.toString());
  if (includeDeleted) params.append('show_deleted', 'true');

  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = `/task_forms_all${queryString}`;

  const response = await client.get<FormTableResponse>(url);
  const { data } = response;
  return data;
}

async function getFormsForTaskAssignFilter() {
  const response = await client.get<FormTableResponse>('/task_forms_for_task_assign_filter');
  return response.data;
}

async function getFormSearch(page: number, search: string) {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('search', search);

  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = `/react_form_index${queryString}`;

  const response = await client.get<FormSearchResponse>(url);
  const { data } = response;
  return data;
}

async function getTaskFormsFolders() {
  const response = await client.get<TaskFormFolder[]>('/task_forms_folders.json');
  const { data } = response;
  return data;
}

async function getTaskFormsRootTable() {
  const response = await client.get<TaskForm[]>('/task_forms_root_table.json');
  const { data } = response;
  return data;
}

async function getTaskFormsTemplates() {
  try {
    const response = await client.get<TaskForm[]>('/task_forms_templates.json');
    const { data } = response;
    return data;
  } catch {
    return [];
  }
}

async function getTemplatesUseCases(templateShareId?: string) {
  const response = await client.get<TemplateUseCase[]>('/task_forms/onboarding_templates', {
    params: templateShareId ? { template_share_id: templateShareId } : undefined,
  });
  const { data } = response;
  return data;
}

async function getRecommendedTemplated() {
  const response = await client.get<RecommendedTemplate[]>('/task_forms/recommended_templates');
  const { data } = response;
  return data;
}

async function toggleHideTaskForm(formId: number) {
  const response = await client.patch<TaskForm>(`/task_forms/${formId}/toggle_hide_form_react`);
  const { data } = response;
  return data;
}

async function toggleScrollTaskForm(formId: number, active: boolean) {
  const response = await client.patch<TaskForm>(`/task_forms/${formId}/activate_scroll`, {
    active,
  });
  const { data } = response;
  return data;
}

async function bulkUpdateFormVisibility(formIds: number[], hide: boolean) {
  const response = await client.post<TaskForm[]>(`/task_forms/bulk_update_visibility`, {
    task_form_ids: formIds,
    hide,
  });
  const { data } = response;
  return data;
}

async function cloneTaskForm(formId: number) {
  const response = await client.post<TaskForm>(`/task_forms/${formId}/clone_form_react`);
  const { data } = response;
  return data;
}

async function deleteTaskForm(formId: number) {
  const response = await client.delete<TaskForm>(`/task_forms/${formId}/destroy_form_react`);
  const { data } = response;
  return data;
}

async function bulkDeleteForms(formIds: number[]) {
  const response = await client.post('/task_forms/bulk_delete', { task_form_ids: formIds });
  const { data } = response;
  return data;
}

async function renameFolder(folderId: number, name: string) {
  const response = await client.patch<TaskFormFolder>(`/form_folders/${folderId}`, { name });
  const { data } = response;
  return data;
}

async function createFolder(name: string, taskForms: number[]) {
  const response = await client.post<TaskFormFolder>('/form_folders', {
    name,
    task_forms_ids: taskForms,
  });
  const { data } = response;
  return data;
}

async function deleteFolder(folderId: number) {
  const response = await client.delete(`/form_folders/${folderId}`);
  const { data } = response;
  return data;
}

async function moveForms({ formIds, destinationId }: { formIds: number[]; destinationId: number }) {
  const response = await client.post('/task_forms/move_forms.json', {
    task_forms_ids: formIds,
    folder_id: destinationId,
  });
  const { data } = response;
  return data;
}
async function uploadImage(image: File, shareToken: string | undefined) {
  const imageData = new FormData();
  imageData.append('file', image);
  if (shareToken) imageData.append('share_token', shareToken);

  const response = await client.post<{ url: string }>(
    `/upload_image_to_s3${shareToken ? `_public` : ''}`,
    imageData,
    {
      headers: {
        'content-type': 'multipart/form-data',
      },
    },
  );
  const { data } = response;
  return data;
}

async function getTaskFormSignatureDataGroups(formId: number) {
  const response = await client.get<TaskFormSignatureDataGroups>(
    `/task_forms/${formId}/signatures/settings`,
  );
  const { data } = response;
  return data;
}

async function updateTaskFormSignatureDataGroups(
  formId: number,
  signatureRequestBody: TaskFormSignatureDataGroupsBody,
) {
  const response = await client.put<TaskFormSignatureDataGroups>(
    `/task_forms/${formId}/signatures/settings`,
    signatureRequestBody,
  );
  const { data } = response;
  return data;
}

async function getTaskFormEmailInfo(formId: number) {
  const response = await client.get<TaskFormEmailInfo>(`/task_forms_email_info/${formId}`);
  const { data } = response;
  return data;
}

async function updateTaskFormEmailInfo({
  formId,
  emailRequestBody,
}: {
  formId: number;
  emailRequestBody: TaskFormEmailInfo;
}) {
  const response = await client.post<TaskFormEmailInfo>(`/task_forms_update_email_info/${formId}`, {
    ...emailRequestBody,
  });
  const { data } = response;
  return data;
}

async function getDynamicSignatureTaskFormQuestions(formId: number) {
  const response = await client.get<DynamicSignatureFormQuestion[]>(
    `/task_forms/${formId}/dynamic_signature_questions`,
  );
  const { data } = response;
  return data;
}

async function importFormTemplate(body: ImportFormTemplateBody) {
  const response = await client.post<ImportFormTemplateResponse>('/app/import_form', body);
  const { data } = response;
  return data;
}

async function validateDynamicSignatureQuestionModification(
  formId: number,
  params: DynamicSignatureFormData,
) {
  const response = await client.get<ValidateDynamicSignatureQuestionModificationResponse>(
    `/task_forms/${formId}/validate_dynamic_signature_question_modification`,
    { params },
  );
  const { data } = response;
  return data.is_modifying_dynamic_question;
}

async function verifyFormName(formId: string, formName: string) {
  const params = new URLSearchParams();

  params.append('form_id', formId);
  params.append('form_name', encodeURIComponent(formName));

  const response = await client.get<{ valid: boolean }>(
    `/task_forms_verify_form_name?${params.toString()}`,
  );
  const { data } = response;
  return data;
}

async function getFolderFormsPendingSignatures() {
  const response = await client.get<Record<string, number[]>>('/form_folders_pending_signatures');
  const { data } = response;
  return data;
}

async function getActiveFormsCount() {
  const response = await client.get<{ active_forms_count: number }>('/active_forms_count');
  const { data } = response;
  return data;
}

async function lockEditing(formId: number) {
  const response = await client.patch<TaskForm>(`/task_forms/${formId}/lock_editing`);
  const { data } = response;
  return data;
}

async function unLockEditing(formId: number) {
  const response = await client.patch<TaskForm>(`/task_forms/${formId}/unlock_editing`);
  const { data } = response;
  return data;
}

export const taskFormsApi = {
  getTaskForms,
  getAllTaskForms,
  getFormsForTaskAssignFilter,
  toggleHideTaskForm,
  bulkUpdateFormVisibility,
  cloneTaskForm,
  deleteTaskForm,
  bulkDeleteForms,
  getTaskFormsTemplates,
  getTaskFormsFolders,
  getTaskFormsRootTable,
  getFormSearch,
  renameFolder,
  createFolder,
  deleteFolder,
  moveForms,
  uploadImage,
  getTaskFormEmailInfo,
  updateTaskFormEmailInfo,
  getDynamicSignatureTaskFormQuestions,
  verifyFormName,
  getFolderFormsPendingSignatures,
  updateTaskFormSignatureDataGroups,
  getTaskFormSignatureDataGroups,
  getTemplatesUseCases,
  getRecommendedTemplated,
  importFormTemplate,
  getActiveFormsCount,
  toggleScrollTaskForm,
  lockEditing,
  unLockEditing,
  validateDynamicSignatureQuestionModification,
};
